import React, { useState, useEffect } from 'react';
import { getDraftPoolPlayers } from '../../DatabaseAPI';

const PlayerPoolModal = ({ isOpen, onClose, teamId, position }) => {
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && teamId && position) {
            fetchPlayers();
        }
    }, [isOpen, teamId, position]);

    const fetchPlayers = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getDraftPoolPlayers(teamId, position);
            if (response.success) {
                setPlayers(response.all_players || []);
            } else {
                setError(response.message || 'Failed to load players');
            }
        } catch (err) {
            setError(err.message || 'Failed to load players');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const getRarityColor = (rarity) => {
        switch (rarity) {
            case 'LEGENDARY': return '#FFD700'; // Gold
            case 'RARE': return '#9A4FFF'; // Purple
            case 'COMMON': return '#8B8B8B'; // Gray
            default: return '#3498db'; // Blue
        }
    };

    const groupedPlayers = {
        LEGENDARY: players.filter(p => p.rarity_tier === 'LEGENDARY'),
        RARE: players.filter(p => p.rarity_tier === 'RARE'),
        COMMON: players.filter(p => p.rarity_tier === 'COMMON')
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                zIndex: 2000,
                padding: '60px 20px 20px 20px',
                overflowY: 'auto'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: '#2c3e50',
                    border: '3px solid #3498db',
                    borderRadius: '12px',
                    padding: '30px',
                    maxWidth: '900px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    position: 'relative'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        background: 'rgba(231, 76, 60, 0.8)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '35px',
                        height: '35px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        cursor: 'pointer',
                        color: 'white',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseOver={e => e.target.style.backgroundColor = 'rgba(192, 57, 43, 1)'}
                    onMouseOut={e => e.target.style.backgroundColor = 'rgba(231, 76, 60, 0.8)'}
                >
                    ×
                </button>

                {/* Header */}
                <h2 style={{
                    margin: '0 0 20px 0',
                    color: '#3498db',
                    fontSize: '28px',
                    textAlign: 'center',
                    borderBottom: '2px solid #3498db',
                    paddingBottom: '15px'
                }}>
                    {position} Draft Pool
                </h2>

                {loading && (
                    <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px' }}>
                        Loading players...
                    </div>
                )}

                {error && (
                    <div style={{
                        textAlign: 'center',
                        padding: '20px',
                        backgroundColor: 'rgba(231, 76, 60, 0.2)',
                        borderRadius: '8px',
                        color: '#e74c3c',
                        marginBottom: '20px'
                    }}>
                        {error}
                    </div>
                )}

                {!loading && !error && players.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px', color: '#95a5a6' }}>
                        No players found in the draft pool for this position.
                    </div>
                )}

                {!loading && !error && players.length > 0 && (
                    <div>
                        {/* Legendary Players */}
                        {groupedPlayers.LEGENDARY.length > 0 && (
                            <div style={{ marginBottom: '30px' }}>
                                <h3 style={{
                                    color: getRarityColor('LEGENDARY'),
                                    fontSize: '22px',
                                    marginBottom: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    <span>✨</span>
                                    Legendary ({groupedPlayers.LEGENDARY.length})
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                    gap: '15px'
                                }}>
                                    {groupedPlayers.LEGENDARY.map((player, index) => (
                                        <PlayerCard key={index} player={player} rarity="LEGENDARY" />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Rare Players */}
                        {groupedPlayers.RARE.length > 0 && (
                            <div style={{ marginBottom: '30px' }}>
                                <h3 style={{
                                    color: getRarityColor('RARE'),
                                    fontSize: '22px',
                                    marginBottom: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    <span>💎</span>
                                    Rare ({groupedPlayers.RARE.length})
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                    gap: '15px'
                                }}>
                                    {groupedPlayers.RARE.map((player, index) => (
                                        <PlayerCard key={index} player={player} rarity="RARE" />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Common Players */}
                        {groupedPlayers.COMMON.length > 0 && (
                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{
                                    color: getRarityColor('COMMON'),
                                    fontSize: '22px',
                                    marginBottom: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    <span>📦</span>
                                    Common ({groupedPlayers.COMMON.length})
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                    gap: '15px'
                                }}>
                                    {groupedPlayers.COMMON.map((player, index) => (
                                        <PlayerCard key={index} player={player} rarity="COMMON" />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const PlayerCard = ({ player, rarity }) => {
    const getRarityColor = (rarity) => {
        switch (rarity) {
            case 'LEGENDARY': return '#FFD700';
            case 'RARE': return '#9A4FFF';
            case 'COMMON': return '#8B8B8B';
            default: return '#3498db';
        }
    };

    return (
        <div style={{
            backgroundColor: '#34495e',
            border: `2px solid ${getRarityColor(rarity)}`,
            borderRadius: '8px',
            padding: '15px',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            cursor: 'default'
        }}
        onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = `0 6px 20px ${getRarityColor(rarity)}40`;
        }}
        onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
        }}
        >
            <div style={{
                fontWeight: 'bold',
                fontSize: '16px',
                marginBottom: '8px',
                color: 'white'
            }}>
                {player.name}
            </div>
            
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: '#bdc3c7',
                marginBottom: '8px'
            }}>
                <span>{player.position} • {player.team}</span>
                <span style={{ color: getRarityColor(rarity), fontWeight: 'bold' }}>
                    #{player.position_rank}
                </span>
            </div>

            {player.matchup_display && (
                <div style={{
                    fontSize: '12px',
                    color: '#95a5a6',
                    marginBottom: '8px'
                }}>
                    {player.matchup_display}
                </div>
            )}

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: '8px',
                borderTop: '1px solid #4a5f7f'
            }}>
                <span style={{ fontSize: '12px', color: '#95a5a6' }}>Projected:</span>
                <span style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#27ae60'
                }}>
                    {player.projected_points.toFixed(1)} pts
                </span>
            </div>
        </div>
    );
};

export default PlayerPoolModal;